/**
 * Admin-managed broadcast announcements — the modal EverGPT shows to every user
 * on load (today's hardcoded "conversation retention" notice, generalised).
 *
 * Distinct from the backend's `notifications` domain, which is a *per-user*
 * inbox for job/file events. These are one-to-many editorial messages authored
 * in Admin panel → Announcements and published to the whole user base.
 *
 * Two models live in this file:
 *
 * - the **domain** model (`IAnnouncement`, `IAnnouncementContent`) the UI is
 *   written against, and
 * - the **wire** model (`I*Wire`) EVGPT-1725 actually serves.
 *
 * They differ enough to need a translation layer — see `modules/announcementMapping`.
 * The wire keeps one opaque `data` object for presentation and a handful of
 * scalar columns it filters on; the domain model flattens both into the shape
 * the editor and the modal already read.
 */

/** Header artwork + accent colour of the modal. `custom` uses `header_image_url`. */
export const ANNOUNCEMENT_MESSAGE_TYPES = [
  'warning',
  'critical',
  'new_feature',
  'info',
  'success',
  'custom',
] as const

export type AnnouncementMessageType =
  (typeof ANNOUNCEMENT_MESSAGE_TYPES)[number]

/** How often a user who has not dismissed the announcement sees it again. */
export const ANNOUNCEMENT_DISPLAY_FREQUENCIES = [
  'until_dismissed',
  'once',
  'every_7_days',
  'once_per_session',
] as const

export type AnnouncementDisplayFrequency =
  (typeof ANNOUNCEMENT_DISPLAY_FREQUENCIES)[number]

/**
 * Lifecycle as the UI models it. Maps to the wire's single `is_active` flag —
 * see `AnnouncementStatus` handling in `modules/announcementMapping`.
 */
export type AnnouncementStatus = 'draft' | 'published'

/** The editable content of an announcement — the half that gets published. */
export interface IAnnouncementContent {
  message_type: AnnouncementMessageType
  /**
   * Body copy as sanitised HTML. The authoring editor emits a deliberately tiny
   * subset — `h3`, `p`, `strong`, `em`, `a[href]`, `br`, plus `style="text-align:…"`.
   * Sanitised on write (client + server) and again on read before rendering.
   */
  body_html: string
  /** Only meaningful when `message_type === 'custom'`. */
  header_image_url: string | null
  /** Renders the "Don't remind me again" checkbox in the modal footer. */
  is_dismissal_remembered: boolean
  /** Primary button copy. Empty/null falls back to "Got it". Max 20 chars. */
  button_label: string | null
  /** Days the announcement stays live after it starts. `null` = never expires. */
  display_duration_days: number | null
  display_frequency: AnnouncementDisplayFrequency
  /**
   * Unix seconds at which the announcement should start being served, or `null`
   * to start the moment it is published. An absolute instant — the admin picks a
   * local date/time and the client converts, so no timezone travels on the wire.
   */
  scheduled_start_at: number | null
}

/** Admin row from `GET /admin/announcements`. Content fields are the *working draft*. */
export interface IAnnouncement extends IAnnouncementContent {
  id: string
  /** Admin-facing label in the announcement list. Never shown to end users. */
  name: string
  status: AnnouncementStatus
  /**
   * Unix seconds of the last publish, `null` while never published.
   * (The API represents every timestamp as an integer — see `TimestampMixin`.)
   */
  published_at: number | null
  /**
   * When the display window opens: `scheduled_start_at` if set, else `published_at`.
   * `null` while never published.
   */
  starts_at: number | null
  /** `starts_at + display_duration_days`; `null` when published with no expiry. */
  expires_at: number | null
  created_at: number
  updated_at: number
}

/**
 * `PATCH /admin/announcements/{id}` as the UI issues it.
 *
 * `content` is deliberately all-or-nothing: it lands in the wire's `data`
 * object, which the server *replaces* rather than merges, so a partial object
 * would silently drop every key it left out.
 */
export interface IAnnouncementUpdate {
  name?: string
  content?: IAnnouncementContent
  /** Publish (`true`) or withdraw (`false`). */
  isActive?: boolean
  /**
   * Overrides the `display_duration` that `content` would otherwise mirror.
   * Only `AnnouncementsApi.publish` sets it — see `publishWindowHours`.
   */
  displayDurationHours?: number
}

/**
 * The trimmed, user-facing projection of an active announcement, mapped from a
 * row of `GET /announcements`. Deliberately omits admin-only fields (name,
 * status) so nothing internal reaches the client.
 */
export interface IActiveAnnouncement extends IAnnouncementContent {
  id: string
  published_at: number
}

// #region Wire

/**
 * The opaque presentation blob. The server types it `dict[str, Any]` and never
 * inspects it, so it is the frontend's own storage — but that also means
 * nothing validates it, and every read has to treat each key as missing or
 * mistyped. `modules/announcementMapping` is the only place allowed to.
 */
export type IAnnouncementData = Record<string, unknown>

/**
 * A row from `GET /admin/announcements`.
 *
 * `repeat`, `display_duration` and `show_on_login` are the only display
 * settings the server can act on; the mapper mirrors the richer domain fields
 * onto them, which is what makes the server's own visibility filtering agree
 * with what the admin picked.
 */
export interface IAnnouncementWire {
  id: string
  title: string
  data: IAnnouncementData
  is_active: boolean
  published_at: number | null
  /** Cooldown in **hours** after the user has seen it. `0` = no cooldown. */
  repeat: number
  /** Visibility window in **hours** from `published_at`. Always `>= 1`. */
  display_duration: number
  /** Reappears on every login, bypassing the `repeat` cooldown. */
  show_on_login: boolean
  /** Server-computed: `published_at + display_duration` has passed. */
  is_expired: boolean
  created_at: number
  updated_at: number
}

/** `POST /admin/announcements`. `repeat` and `display_duration` are required. */
export interface IAnnouncementCreateWire {
  title: string
  data: IAnnouncementData
  is_active: boolean
  repeat: number
  display_duration: number
  show_on_login: boolean
}

/** `PATCH /admin/announcements/{id}` — sparse, and `extra="forbid"`. */
export type IAnnouncementUpdateWire = Partial<IAnnouncementCreateWire>

/**
 * A row from `GET /announcements` — the announcements visible to the calling
 * user, already filtered server-side by publication, expiry and their own
 * dismissal state.
 */
export interface IUserAnnouncementWire {
  id: string
  title: string
  data: IAnnouncementData
  published_at: number
  show_on_login: boolean
}

/** `PATCH /announcements/{id}` — records that the user saw or dismissed it. */
export interface IAnnouncementDismissResponseWire {
  announcement_id: string
  /** Unix seconds the view was recorded; what the `repeat` cooldown counts from. */
  last_saw: number
  is_dismissed: boolean
}

// #endregion
